import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { translations } from '../../../configs/translations'
import { ClientApi } from '../../../services/clientApi'
import { useStore } from '../../../store/store'
import { NuiMessages } from '../../../utils/ClientEvents'
import { MainHint } from '../mainHint/MainHint'
import { DriftEventAnnounce } from './announce/DriftEventAnnounce'
import { EventAnnounce } from './announce/DriftEventAnnounce.types'
import { PreparationCountdown } from './preparationCountDown/PreparationCountdown'

interface EventPreparationProps {
	eventSource: EventSource | null
}

interface MainHintState {
	id: number
	text: string
	isToRegister: boolean
}

export interface EventCountDownState {
	id: number
	remainingUntilStart: number
	playersCount: number
	maxPlayers: number
	shown: boolean
	eventName: string
}

type SendToFrontEvent = 'HINT_TO_REGISTER' | 'HINT_TO_UNREGISTER' | 'NO_HINT'

export const EventPreparation = ({ eventSource }: EventPreparationProps) => {
	const [driftEventAnnounce, setDriftEventAnnounce] = useState<EventAnnounce | null>(null)
	const [mainHint, setMainHint] = useState<MainHintState | null>(null)
	const [eventCountDown, setEventCountDown] = useState<EventCountDownState | null>(null)

	const language = useStore(state => state.language) as keyof typeof translations
	const t = translations[language]

	const announceTimeoutRef = useRef<number | null>(null)
	const announceIdRef = useRef(0)
	const mainHintIdRef = useRef(0)
	const eventCountDownRef = useRef<EventCountDownState | null>(null)

	useEffect(() => {
		ClientApi.on(NuiMessages.SET_MAIN_HINT, ({ hint }: { hint: SendToFrontEvent }) => {
			if (hint === 'NO_HINT') {
				setMainHint(null)
				return
			}

			mainHintIdRef.current += 1

			setMainHint({
				id: mainHintIdRef.current,
				text: hint === 'HINT_TO_REGISTER' ? t.event_registration_hint : t.event_unregistration_hint,
				isToRegister: hint === 'HINT_TO_REGISTER'
			})
		})

		ClientApi.on(NuiMessages.EVENT_REGISTER_ERROR, ({ error }: { error: string }) => {
			toast.error(
				t[error as keyof typeof t] ?? t.error_unknown,
				{ className: 'notification-error' }
			)
		})
	}, [t])

	useEffect(() => {
		if (!eventSource) return

		const announceHandler = (e: Event) => {
			const messageEvent = e as MessageEvent
			const data = JSON.parse(messageEvent.data) as Omit<EventAnnounce, 'id'>

			announceIdRef.current += 1

			const getNextEventAnnounce = (): EventAnnounce => {
				if (data.event && data.event.eventName) {
					return {
						id: announceIdRef.current,
						event: {
							eventName: data.event.eventName,
							instruction: data.event.instruction
						}
					}
				} else if (data.other && data.other.title) {
					return {
						id: announceIdRef.current,
						other: {
							title: data.other.title,
							text: data.other.text
						}
					}
				}

				return {
					id: announceIdRef.current,
					other: {
						title: "",
						text: ""
					}
				}
			}

			const nextEventAnnounce = getNextEventAnnounce()

			setDriftEventAnnounce(nextEventAnnounce)

			if (announceTimeoutRef.current) {
				window.clearTimeout(announceTimeoutRef.current)
			}

			const DRIFT_ANNOUNCE_LIFETIME_MS = 4400
			announceTimeoutRef.current = window.setTimeout(() => {
				setDriftEventAnnounce(current =>
					current?.id === nextEventAnnounce.id ? null : current
				)
			}, DRIFT_ANNOUNCE_LIFETIME_MS)
		}

		const countDownHandler = (e: Event) => {
			const messageEvent = e as MessageEvent
			const raw = JSON.parse(messageEvent.data) as {
				eventName: string
				remainingUntilStart: number
				playersCount: number
				maxPlayers: number
				shown: boolean
			}

			setEventCountDown(current => {
				if (!current) {
					return {
						id: 1,
						eventName: raw.eventName,
						remainingUntilStart: raw.remainingUntilStart,
						playersCount: raw.playersCount,
						maxPlayers: raw.maxPlayers,
						shown: raw.shown,
					}
				}

				const remainingUntilStartChanged = current.remainingUntilStart !== raw.remainingUntilStart
				const playersCountChanged = current.playersCount !== raw.playersCount
				const maxPlayersChanged = current.maxPlayers !== raw.maxPlayers
				const shownChanged = current.shown !== raw.shown
				const eventNameChanged = current.eventName !== raw.eventName

				return {
					...current,
					eventName: eventNameChanged ? raw.eventName : current.eventName,
					id: remainingUntilStartChanged ? current.id + 1 : current.id,
					remainingUntilStart: remainingUntilStartChanged ? raw.remainingUntilStart : current.remainingUntilStart,
					playersCount: playersCountChanged ? raw.playersCount : current.playersCount,
					maxPlayers: maxPlayersChanged ? raw.maxPlayers : current.maxPlayers,
					shown: shownChanged ? raw.shown : current.shown
				}
			})
		}

		eventSource.addEventListener('FREEROAM_ANNOUNCE', announceHandler)
		eventSource.addEventListener('FREEROAM_COUNTDOWN', countDownHandler)
		eventSource.addEventListener('FREEROAM_EVENT_END', () => {
			setDriftEventAnnounce(null)
			setEventCountDown(null)
			setMainHint(null)
		})

		return () => {
			eventSource.removeEventListener('FREEROAM_ANNOUNCE', announceHandler)
			eventSource.removeEventListener('FREEROAM_COUNTDOWN', countDownHandler)
			eventSource.removeEventListener('FREEROAM_EVENT_END', () => {
				setDriftEventAnnounce(null)
				setEventCountDown(null)
				setMainHint(null)
			})

			if (announceTimeoutRef.current) {
				window.clearTimeout(announceTimeoutRef.current)
			}
		}
	}, [eventSource])

	return (
		<div>
			{driftEventAnnounce && (
				<DriftEventAnnounce
					key={driftEventAnnounce.id}
					data={driftEventAnnounce}
				/>
			)}

			{mainHint && (
				<MainHint text={mainHint.text} />
			)}

			{eventCountDown && eventCountDown.shown && (
				<PreparationCountdown
					key={eventCountDown.id}
					data={eventCountDown}
				/>
			)}
		</div>
	)
}